import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import {
  Plus,
  Search,
  User,
  Star,
  ChevronRight,
  ExternalLink,
  Filter,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  Target,
  LayoutGrid,
  List,
  MessageSquare,
  FileText,
  GripVertical,
  DollarSign,
  Edit2,
  Trash2,
  Image,
  Upload,
} from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

// Pipeline stages for Kanban
const KANBAN_STAGES = [
  { id: "new", label: "New", color: "bg-zinc-600", borderColor: "border-zinc-500" },
  { id: "portfolio_review", label: "Portfolio Review", color: "bg-blue-600", borderColor: "border-blue-500" },
  { id: "potential_fit", label: "Potential Fit", color: "bg-cyan-600", borderColor: "border-cyan-500" },
  { id: "test_assigned", label: "Test Assigned", color: "bg-yellow-600", borderColor: "border-yellow-500" },
  { id: "test_review", label: "Test Review", color: "bg-orange-600", borderColor: "border-orange-500" },
  { id: "negotiation", label: "Negotiation", color: "bg-purple-600", borderColor: "border-purple-500" },
];

// Final stages (separate section)
const FINAL_STAGES = [
  { id: "approved", label: "Approved", color: "bg-green-600", borderColor: "border-green-500" },
  { id: "rejected", label: "Rejected", color: "bg-red-600", borderColor: "border-red-500" },
  { id: "second_chance", label: "Second Chance", color: "bg-amber-600", borderColor: "border-amber-500" },
];

const ALL_STAGES = [...KANBAN_STAGES, ...FINAL_STAGES];

const SOURCES = [
  { id: "manual", label: "Manual Entry" },
  { id: "application", label: "Application" },
  { id: "referral", label: "Referral" },
  { id: "external", label: "External" },
];

// Default specializations (will be overridden by API)
const DEFAULT_SPECIALIZATIONS = [
  { id: "exterior", label: "Exterior" },
  { id: "interior", label: "Interior" },
  { id: "floor_plan", label: "Floor Plans" },
  { id: "animation", label: "Animation" },
  { id: "modeller", label: "3D Modeling" },
  { id: "vr_tours", label: "VR Tours" },
  { id: "360_views", label: "360° Views" },
  { id: "aerial", label: "Aerial" },
];

// Specialization labels mapping
const SPEC_LABELS = {
  exterior: "Exterior",
  interior: "Interior",
  floor_plan: "Floor Plans",
  animation: "Animation",
  modeller: "3D Modeling",
  vr_tours: "VR Tours",
  "360_views": "360° Views",
  aerial: "Aerial",
};

// Rating options 1-10
const RATINGS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

// Get portfolio icon based on URL - enhanced version
const getPortfolioIcon = (url) => {
  if (!url) return null;
  const lowerUrl = url.toLowerCase();
  
  // Platform-specific icons
  if (lowerUrl.includes("artstation")) return { icon: "🎨", label: "ArtStation", color: "text-blue-400" };
  if (lowerUrl.includes("behance")) return { icon: "🅱️", label: "Behance", color: "text-blue-500" };
  if (lowerUrl.includes("instagram")) return { icon: "📷", label: "Instagram", color: "text-pink-400" };
  if (lowerUrl.includes("linkedin")) return { icon: "💼", label: "LinkedIn", color: "text-blue-600" };
  if (lowerUrl.includes("dribbble")) return { icon: "🏀", label: "Dribbble", color: "text-pink-500" };
  if (lowerUrl.includes("drive.google")) return { icon: "📁", label: "Google Drive", color: "text-yellow-400" };
  if (lowerUrl.includes("dropbox")) return { icon: "📦", label: "Dropbox", color: "text-blue-400" };
  
  // File types (uploaded images)
  if (lowerUrl.match(/\.(jpg|jpeg|png|gif|webp)$/i)) return { icon: "🖼️", label: "Image", color: "text-green-400" };
  if (lowerUrl.match(/\.(pdf)$/i)) return { icon: "📄", label: "PDF", color: "text-red-400" };
  
  // Personal website detection
  if (lowerUrl.includes("portfolio") || lowerUrl.match(/\.(com|io|art|design|me)$/)) {
    return { icon: "🌐", label: "Website", color: "text-purple-400" };
  }
  
  return { icon: "🔗", label: "Link", color: "text-zinc-400" };
};

// Format service rate for compact display
const formatServiceRate = (rate) => {
  if (!rate) return null;
  const currency = rate.currency === "USD" ? "$" : rate.currency === "EUR" ? "€" : "₴";
  // Shorten service name: "Exterior Renderings" -> "EXT", "Interior Renderings" -> "INT"
  const shortNames = {
    "exterior": "EXT",
    "interior": "INT",
    "360": "360",
    "animation": "ANIM",
    "modeling": "3D",
    "floor": "FLR",
    "site": "SITE",
    "aerial": "AER",
    "vr": "VR",
    "tour": "TOUR",
  };
  
  let shortName = "SVC";
  const nameLower = (rate.service_type_name || "").toLowerCase();
  for (const [key, val] of Object.entries(shortNames)) {
    if (nameLower.includes(key)) {
      shortName = val;
      break;
    }
  }
  
  return {
    short: `${shortName} ${currency}${rate.rate}`,
    full: `${rate.service_type_name}: ${currency}${rate.rate}`,
    name: rate.service_type_name,
    price: `${currency}${rate.rate}`,
  };
};

// Format relative time
const getRelativeTime = (dateStr) => {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
};

// Candidate Card Component for Kanban - with Hero Image and Quick Edit
const CandidateCard = ({ candidate, onDragStart, onDragEnd, onClick, onQuickEdit, onDelete, specializations = [] }) => {
  const profile = candidate.candidate_profile || {};
  const portfolioLinks = profile.portfolio_links || [];
  const serviceRates = profile.service_rates || [];
  const workSamples = profile.work_samples || [];
  const workSamplesData = profile.work_samples_data || [];
  // Use thumbnail URL if available, otherwise construct from original URL
  const heroImage = workSamplesData[0]?.thumbnail || (workSamples[0] ? `${workSamples[0]}/thumb` : null);
  const lastNote = (candidate.evaluation_history || []).slice(-1)[0];
  const lastActivity = candidate.updated_at || candidate.created_at;
  const [editOpen, setEditOpen] = useState(false);
  const [selectedSpecs, setSelectedSpecs] = useState(candidate.specializations || []);
  const [selectedRating, setSelectedRating] = useState(profile.skill_rating || null);
  const [portfolioUrl, setPortfolioUrl] = useState("");
  const [noteText, setNoteText] = useState("");
  const [imageError, setImageError] = useState(false);
  
  // Format service rates for display
  const formattedRates = serviceRates.slice(0, 3).map(formatServiceRate).filter(Boolean);
  const allRates = serviceRates.map(formatServiceRate).filter(Boolean);

  // Calculate Talent Score
  const talentScore = (() => {
    const scores = [];
    if (profile.skill_rating) scores.push({ value: profile.skill_rating, weight: 0.4 });
    const latestNotes = (candidate.evaluation_history || []).slice(-3);
    latestNotes.forEach(n => {
      if (n.technical_skill) scores.push({ value: n.technical_skill, weight: 0.3 });
      if (n.quality_rating) scores.push({ value: n.quality_rating, weight: 0.2 });
      if (n.style_compatibility) scores.push({ value: n.style_compatibility, weight: 0.1 });
    });
    if (scores.length === 0) return null;
    const totalWeight = scores.reduce((sum, s) => sum + s.weight, 0);
    const weightedSum = scores.reduce((sum, s) => sum + (s.value * s.weight), 0);
    return (weightedSum / totalWeight).toFixed(1);
  })();

  const handleQuickSave = async (field, value) => {
    if (onQuickEdit) {
      await onQuickEdit(candidate.id, field, value);
    }
  };

  const handleEditClick = (e) => {
    e.stopPropagation();
    setEditOpen(!editOpen);
  };

  const toggleSpec = (specId) => {
    const newSpecs = selectedSpecs.includes(specId) 
      ? selectedSpecs.filter(s => s !== specId)
      : [...selectedSpecs, specId];
    setSelectedSpecs(newSpecs);
    handleQuickSave("specializations", newSpecs);
  };
  
  return (
    <TooltipProvider>
      <div
        draggable={!editOpen}
        onDragStart={(e) => !editOpen && onDragStart(e, candidate)}
        onDragEnd={onDragEnd}
        onClick={() => !editOpen && onClick(candidate.id)}
        className={cn(
          "bg-zinc-800 rounded-lg overflow-hidden hover:bg-zinc-750 border border-zinc-700 hover:border-zinc-600 transition-all group relative",
          editOpen ? "cursor-default" : "cursor-grab active:cursor-grabbing"
        )}
        data-testid={`candidate-card-${candidate.id}`}
      >
        {/* Hero Image */}
        {heroImage && !imageError ? (
          <div className="relative h-28 w-full">
            <img 
              src={heroImage} 
              alt={candidate.name} 
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent" />
            {/* Talent Score on image */}
            {talentScore && (
              <div className={cn(
                "absolute top-2 right-2 flex items-center gap-0.5 px-2 py-1 rounded-full",
                parseFloat(talentScore) >= 8 ? "bg-green-500" :
                parseFloat(talentScore) >= 6 ? "bg-yellow-500" :
                "bg-red-500"
              )}>
                <Star className="h-3 w-3 fill-white text-zinc-50" />
                <span className="text-xs font-bold text-zinc-50">{talentScore}</span>
              </div>
            )}
            {/* Edit button on image */}
            <Button
              size="sm"
              variant="ghost"
              onClick={handleEditClick}
              className={cn(
                "absolute top-2 left-2 h-7 w-7 p-0 rounded-full",
                editOpen ? "bg-orange-500 text-white" : "bg-black/50 text-white opacity-0 group-hover:opacity-100"
              )}
            >
              <Edit2 className="h-3 w-3" />
            </Button>
            {/* Delete button on image */}
            {candidate.candidate_stage !== "approved" && (
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete && onDelete(candidate);
                }}
                className="absolute top-2 left-10 h-7 w-7 p-0 rounded-full bg-black/50 text-red-400 opacity-0 group-hover:opacity-100 hover:bg-red-500 hover:text-white"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        ) : (
          <div className="relative h-20 w-full bg-gradient-to-br from-zinc-700 to-zinc-800 flex items-center justify-center">
            <User className="h-10 w-10 text-zinc-600" />
            {talentScore && (
              <div className={cn(
                "absolute top-2 right-2 flex items-center gap-0.5 px-2 py-1 rounded-full",
                parseFloat(talentScore) >= 8 ? "bg-green-500" :
                parseFloat(talentScore) >= 6 ? "bg-yellow-500" :
                "bg-red-500"
              )}>
                <Star className="h-3 w-3 fill-white text-zinc-50" />
                <span className="text-xs font-bold text-zinc-50">{talentScore}</span>
              </div>
            )}
            <Button
              size="sm"
              variant="ghost"
              onClick={handleEditClick}
              className={cn(
                "absolute top-2 left-2 h-7 w-7 p-0 rounded-full",
                editOpen ? "bg-orange-500 text-white" : "bg-black/50 text-white opacity-0 group-hover:opacity-100"
              )}
            >
              <Edit2 className="h-3 w-3" />
            </Button>
            {/* Delete button when no hero image */}
            {candidate.candidate_stage !== "approved" && (
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete && onDelete(candidate);
                }}
                className="absolute top-2 left-10 h-7 w-7 p-0 rounded-full bg-black/50 text-red-400 opacity-0 group-hover:opacity-100 hover:bg-red-500 hover:text-white"
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        )}
        
        {/* Content */}
        <div className="p-3">
          {/* Name + Specializations */}
          <div className="mb-2">
            <p className="text-zinc-50 font-medium text-sm truncate">{candidate.name}</p>
            {selectedSpecs.length > 0 && !editOpen && (
              <div className="flex flex-wrap gap-1 mt-1">
                {selectedSpecs.map(spec => (
                  <span key={spec} className="text-[9px] text-zinc-400 bg-zinc-700 px-1.5 py-0.5 rounded">
                    {SPEC_LABELS[spec] || spec}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Quick Edit Panel */}
          {editOpen && (
            <div className="mb-3 space-y-2" onClick={(e) => e.stopPropagation()}>
              {/* Rating Dropdown */}
              <div className="flex items-center gap-2">
                <Label className="text-[10px] text-zinc-500 w-12">Rating</Label>
                <Select 
                  value={selectedRating?.toString() || ""} 
                  onValueChange={(v) => {
                    const rating = parseInt(v);
                    setSelectedRating(rating);
                    handleQuickSave("skill_rating", rating);
                  }}
                >
                  <SelectTrigger className="h-7 text-xs bg-zinc-900 border-zinc-700 text-zinc-50 flex-1">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {RATINGS.map(r => (
                      <SelectItem key={r} value={r.toString()} className="text-zinc-50 text-xs">
                        {r} {r >= 8 ? "⭐" : r >= 6 ? "" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Specializations Multi-Select */}
              <div>
                <Label className="text-[10px] text-zinc-500 block mb-1">Specializations</Label>
                <div className="flex flex-wrap gap-1">
                  {specializations.map(spec => (
                    <button
                      key={spec.id}
                      onClick={() => toggleSpec(spec.id)}
                      className={cn(
                        "text-[9px] px-2 py-1 rounded transition-colors",
                        selectedSpecs.includes(spec.id) 
                          ? "bg-orange-500 text-white" 
                          : "bg-zinc-700 text-zinc-400 hover:bg-zinc-600"
                      )}
                    >
                      {spec.label}
                    </button>
                  ))}
                </div>
              </div>
              
              {/* Portfolio Link */}
              <div className="flex items-center gap-1">
                <Input
                  value={portfolioUrl}
                  onChange={(e) => setPortfolioUrl(e.target.value)}
                  className="h-7 text-xs bg-zinc-900 border-zinc-700 text-zinc-50 flex-1"
                  placeholder="Add portfolio link..."
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (portfolioUrl) {
                      handleQuickSave("portfolio_link", portfolioUrl);
                      setPortfolioUrl("");
                    }
                  }}
                  className="h-7 w-7 p-0 text-green-500 hover:text-green-400"
                  disabled={!portfolioUrl}
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
              
              {/* Quick Note */}
              <div className="flex items-center gap-1">
                <Input
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  className="h-7 text-xs bg-zinc-900 border-zinc-700 text-zinc-50 flex-1"
                  placeholder="Add quick note..."
                />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    if (noteText) {
                      handleQuickSave("note", noteText);
                      setNoteText("");
                    }
                  }}
                  className="h-7 w-7 p-0 text-green-500 hover:text-green-400"
                  disabled={!noteText}
                >
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
          
          {/* Service Rates */}
          {!editOpen && formattedRates.length > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="mb-2 flex items-center gap-1 flex-wrap cursor-help">
                  <DollarSign className="h-3 w-3 text-green-500 flex-shrink-0" />
                  {formattedRates.map((rate, i) => (
                    <span key={i} className="text-[10px] text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded">
                      {rate.short}
                    </span>
                  ))}
                  {serviceRates.length > 3 && (
                    <span className="text-[10px] text-zinc-500">+{serviceRates.length - 3}</span>
                  )}
                </div>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="bg-zinc-900 border-zinc-700 p-2">
                <div className="space-y-1">
                  <p className="text-xs font-medium text-zinc-300 mb-1">Service Rates:</p>
                  {allRates.map((rate, i) => (
                    <div key={i} className="text-xs text-zinc-400">
                      {rate.name}: <span className="text-green-400 font-medium">{rate.price}</span>
                    </div>
                  ))}
                </div>
              </TooltipContent>
            </Tooltip>
          )}
          
          {/* Notes Preview */}
          {!editOpen && (lastNote?.notes || lastNote?.note) && (
            <div className="mb-2 px-2 py-1.5 bg-zinc-900/50 rounded text-[10px] text-zinc-400 line-clamp-2 italic">
              "{lastNote.notes || lastNote.note}"
            </div>
          )}
          
          {/* Footer: Portfolio + Activity */}
          {!editOpen && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                {portfolioLinks.length > 0 ? (
                  portfolioLinks.slice(0, 4).map((link, i) => {
                    const info = getPortfolioIcon(link);
                    return (
                      <Tooltip key={i}>
                        <TooltipTrigger asChild>
                          <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className={cn(
                              "text-sm hover:scale-125 transition-transform",
                              info?.color
                            )}
                          >
                            {info?.icon}
                          </a>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="bg-zinc-900 border-zinc-700">
                          <p className="text-xs">{info?.label}</p>
                        </TooltipContent>
                      </Tooltip>
                    );
                  })
                ) : (
                  <span className="text-[10px] text-zinc-600">No portfolio</span>
                )}
              </div>
              
              <div className="flex items-center gap-1 text-[10px] text-zinc-500">
                <Clock className="h-3 w-3" />
                <span>{getRelativeTime(lastActivity)}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </TooltipProvider>
  );
};

// Table Quick Edit Component
const TableQuickEdit = ({ candidate, specializations, onSave }) => {
  const profile = candidate.candidate_profile || {};
  const [rating, setRating] = useState(profile.skill_rating || "");
  const [specs, setSpecs] = useState(candidate.specializations || []);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);

  const toggleSpec = (specId) => {
    setSpecs(prev => 
      prev.includes(specId) ? prev.filter(s => s !== specId) : [...prev, specId]
    );
  };

  const handleSaveRating = async () => {
    if (!rating) return;
    setSaving(true);
    await onSave(candidate.id, "skill_rating", parseInt(rating));
    setSaving(false);
  };

  const handleSaveSpecs = async () => {
    setSaving(true);
    await onSave(candidate.id, "specializations", specs);
    setSaving(false);
  };

  const handleSaveNote = async () => {
    if (!note.trim()) return;
    setSaving(true);
    await onSave(candidate.id, "note", note);
    setNote("");
    setSaving(false);
  };

  return (
    <div className="space-y-3">
      <p className="text-sm font-medium text-zinc-50 mb-2">Quick Edit: {candidate.name}</p>
      
      {/* Rating */}
      <div>
        <Label className="text-[10px] text-zinc-500 block mb-1">Rating (1-10)</Label>
        <div className="flex items-center gap-2">
          <Select value={rating?.toString()} onValueChange={setRating}>
            <SelectTrigger className="w-20 h-7 text-xs bg-zinc-800 border-zinc-700">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              {[1,2,3,4,5,6,7,8,9,10].map(n => (
                <SelectItem key={n} value={n.toString()} className="text-xs">
                  {n} {n >= 8 ? "⭐" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button 
            size="sm" 
            onClick={handleSaveRating}
            disabled={saving || !rating}
            className="h-7 text-xs bg-orange-500 hover:bg-orange-600"
          >
            Save
          </Button>
        </div>
      </div>

      {/* Specializations */}
      <div>
        <Label className="text-[10px] text-zinc-500 block mb-1">Specializations</Label>
        <div className="flex flex-wrap gap-1 mb-2">
          {specializations.map(spec => (
            <button
              key={spec.id}
              onClick={() => toggleSpec(spec.id)}
              className={cn(
                "text-[9px] px-2 py-1 rounded transition-colors",
                specs.includes(spec.id) 
                  ? "bg-orange-500 text-white" 
                  : "bg-zinc-700 text-zinc-400 hover:bg-zinc-600"
              )}
            >
              {spec.label}
            </button>
          ))}
        </div>
        <Button 
          size="sm" 
          onClick={handleSaveSpecs}
          disabled={saving}
          className="h-7 text-xs bg-orange-500 hover:bg-orange-600"
        >
          Save Specs
        </Button>
      </div>

      {/* Quick Note */}
      <div>
        <Label className="text-[10px] text-zinc-500 block mb-1">Add Note</Label>
        <div className="flex gap-2">
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Quick note..."
            className="h-7 text-xs bg-zinc-800 border-zinc-700 flex-1"
          />
          <Button 
            size="sm" 
            onClick={handleSaveNote}
            disabled={saving || !note.trim()}
            className="h-7 text-xs bg-orange-500 hover:bg-orange-600"
          >
            Add
          </Button>
        </div>
      </div>
    </div>
  );
};

// Kanban Column Component
const KanbanColumn = ({ stage, candidates, onDragOver, onDrop, onDragStart, onDragEnd, onCardClick, onQuickEdit, onDelete, isDragOver, specializations }) => {
  return (
    <div 
      className={cn(
        "flex-shrink-0 w-[280px] flex flex-col rounded-lg bg-zinc-900/30 border transition-colors",
        isDragOver ? `${stage.borderColor} bg-zinc-800/50` : "border-zinc-800"
      )}
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, stage.id)}
      data-testid={`kanban-column-${stage.id}`}
    >
      {/* Column Header */}
      <div className={cn("px-3 py-2 rounded-t-lg flex items-center justify-between", stage.color)}>
        <span className="text-zinc-50 text-sm font-medium">{stage.label}</span>
        <Badge variant="secondary" className="bg-white/20 text-zinc-50 text-xs">
          {candidates.length}
        </Badge>
      </div>
      
      {/* Cards Container */}
      <div className="flex-1 p-2 space-y-2 overflow-y-auto max-h-[calc(100vh-380px)] min-h-[200px]">
        {candidates.length === 0 ? (
          <div className="text-center py-8 text-zinc-600 text-xs">
            No candidates
          </div>
        ) : (
          candidates.map((candidate) => (
            <CandidateCard
              key={candidate.id}
              candidate={candidate}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onClick={onCardClick}
              onQuickEdit={onQuickEdit}
              onDelete={onDelete}
              specializations={specializations}
            />
          ))
        )}
      </div>
    </div>
  );
};

const CandidatesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [candidates, setCandidates] = useState([]);
  const [stageCounts, setStageCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [sortBy, setSortBy] = useState("activity"); // "activity", "rating", "name"
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [kpi, setKpi] = useState(null);
  const [viewMode, setViewMode] = useState("kanban"); // "kanban" or "table"
  const [draggedCandidate, setDraggedCandidate] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [specializations, setSpecializations] = useState(DEFAULT_SPECIALIZATIONS);
  const [specFilter, setSpecFilter] = useState("all");
  const [deleteCandidate, setDeleteCandidate] = useState(null);
  
  const [newCandidate, setNewCandidate] = useState({
    name: "",
    email: "",
    phone: "",
    telegram: "",
    portfolio_links: "",
    specializations: [],
    source: "manual",
    source_details: "",
    notes: "",
  });

  const fetchCandidates = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (sourceFilter !== "all") params.append("source", sourceFilter);
      if (specFilter !== "all") params.append("specialization", specFilter);
      if (search) params.append("search", search);
      
      const res = await api.get(`/candidates?${params.toString()}`);
      setCandidates(res.data.candidates || []);
      setStageCounts(res.data.stage_counts || {});
    } catch (error) {
      toast.error("Failed to load candidates");
    } finally {
      setLoading(false);
    }
  };

  const fetchKpi = async () => {
    try {
      const res = await api.get("/recruitment-kpi");
      setKpi(res.data);
    } catch (error) {
      console.error("Failed to load KPI");
    }
  };

  const fetchSpecializations = async () => {
    try {
      const res = await api.get("/staff/specializations");
      const specs = (res.data.specializations || []).map(id => ({
        id,
        label: SPEC_LABELS[id] || id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
      }));
      setSpecializations(specs.length > 0 ? specs : DEFAULT_SPECIALIZATIONS);
    } catch (error) {
      console.error("Failed to load specializations, using defaults");
      setSpecializations(DEFAULT_SPECIALIZATIONS);
    }
  };

  useEffect(() => {
    fetchCandidates();
    fetchKpi();
    fetchSpecializations();
  }, [sourceFilter, specFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCandidates();
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const handleAddCandidate = async () => {
    if (!newCandidate.name.trim()) {
      toast.error("Name is required");
      return;
    }

    try {
      const payload = {
        ...newCandidate,
        portfolio_links: newCandidate.portfolio_links
          ? newCandidate.portfolio_links.split("\n").filter(l => l.trim())
          : [],
      };
      
      await api.post("/candidates", payload);
      toast.success("Candidate added successfully");
      setAddDialogOpen(false);
      setNewCandidate({
        name: "",
        email: "",
        phone: "",
        telegram: "",
        portfolio_links: "",
        specializations: [],
        source: "manual",
        source_details: "",
        notes: "",
      });
      fetchCandidates();
      fetchKpi();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add candidate");
    }
  };

  const getStageConfig = (stageId) => {
    return ALL_STAGES.find(s => s.id === stageId) || ALL_STAGES[0];
  };

  const handleStageChange = async (candidateId, newStage) => {
    try {
      await api.put(`/candidates/${candidateId}/stage?stage=${newStage}`);
      toast.success("Stage updated");
      fetchCandidates();
    } catch (error) {
      toast.error("Failed to update stage");
    }
  };

  // Quick Edit handler for card inline editing
  const handleQuickEdit = async (candidateId, field, value) => {
    try {
      let updateData = {};
      
      if (field === "skill_rating") {
        updateData = { skill_rating: value };
      } else if (field === "specializations") {
        updateData = { specializations: value };
      } else if (field === "portfolio_link") {
        // Get current portfolio links and add new one
        const candidate = candidates.find(c => c.id === candidateId);
        const currentLinks = candidate?.candidate_profile?.portfolio_links || [];
        updateData = { portfolio_links: [...currentLinks, value] };
      } else if (field === "note") {
        // Add a quick note
        const params = new URLSearchParams();
        params.append("note", value);
        await api.post(`/candidates/${candidateId}/notes?${params.toString()}`);
        toast.success("Note added");
        fetchCandidates();
        return;
      }
      
      await api.put(`/candidates/${candidateId}`, updateData);
      toast.success("Updated");
      fetchCandidates();
    } catch (error) {
      toast.error("Failed to update");
    }
  };

  // Delete candidate handler
  const handleDeleteCandidate = async (candidateId) => {
    try {
      await api.delete(`/candidates/${candidateId}`);
      toast.success("Candidate deleted");
      setDeleteCandidate(null);
      fetchCandidates();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete candidate");
    }
  };

  // Calculate Talent Score for sorting
  const calculateTalentScore = (candidate) => {
    const profile = candidate.candidate_profile || {};
    const scores = [];
    if (profile.skill_rating) scores.push({ value: profile.skill_rating, weight: 0.4 });
    const latestNotes = (candidate.evaluation_history || []).slice(-3);
    latestNotes.forEach(n => {
      if (n.technical_skill) scores.push({ value: n.technical_skill, weight: 0.3 });
      if (n.quality_rating) scores.push({ value: n.quality_rating, weight: 0.2 });
      if (n.style_compatibility) scores.push({ value: n.style_compatibility, weight: 0.1 });
    });
    if (scores.length === 0) return 0;
    const totalWeight = scores.reduce((sum, s) => sum + s.weight, 0);
    return scores.reduce((sum, s) => sum + (s.value * s.weight), 0) / totalWeight;
  };

  // Drag & Drop handlers
  const handleDragStart = useCallback((e, candidate) => {
    setDraggedCandidate(candidate);
    e.dataTransfer.setData("text/plain", candidate.id);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleDragEnd = useCallback(() => {
    setDraggedCandidate(null);
    setDragOverStage(null);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, []);

  const handleDrop = useCallback(async (e, newStage) => {
    e.preventDefault();
    setDragOverStage(null);
    
    if (draggedCandidate && draggedCandidate.candidate_stage !== newStage) {
      await handleStageChange(draggedCandidate.id, newStage);
    }
    setDraggedCandidate(null);
  }, [draggedCandidate]);

  const handleColumnDragOver = useCallback((e, stageId) => {
    e.preventDefault();
    setDragOverStage(stageId);
  }, []);

  // Group candidates by stage with sorting
  const candidatesByStage = useMemo(() => {
    const grouped = {};
    
    // Sort function based on sortBy
    const sortCandidates = (list) => {
      return [...list].sort((a, b) => {
        if (sortBy === "rating") {
          return calculateTalentScore(b) - calculateTalentScore(a);
        } else if (sortBy === "name") {
          return (a.name || "").localeCompare(b.name || "");
        } else { // activity (default)
          const dateA = new Date(a.updated_at || a.created_at || 0);
          const dateB = new Date(b.updated_at || b.created_at || 0);
          return dateB - dateA; // Most recent first
        }
      });
    };
    
    ALL_STAGES.forEach(stage => {
      grouped[stage.id] = sortCandidates(candidates.filter(c => c.candidate_stage === stage.id));
    });
    return grouped;
  }, [candidates, sortBy]);

  // Calculate KPI progress
  const kpiProgress = useMemo(() => {
    if (!kpi?.kpi?.current_month_stats) return null;
    const stats = kpi.kpi.current_month_stats;
    const targets = kpi.kpi.monthly_targets || { candidates_added: 10, candidates_approved: 3 };
    
    return {
      added: {
        current: stats.candidates_added || 0,
        target: targets.candidates_added,
        percent: Math.min(100, Math.round(((stats.candidates_added || 0) / targets.candidates_added) * 100))
      },
      approved: {
        current: stats.candidates_approved || 0,
        target: targets.candidates_approved,
        percent: Math.min(100, Math.round(((stats.candidates_approved || 0) / targets.candidates_approved) * 100))
      }
    };
  }, [kpi]);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50">Candidates Pipeline</h1>
          <p className="text-sm text-zinc-400 mt-1">
            {candidates.length} candidates • Drag cards to change stage
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Toggle */}
          <div className="flex items-center bg-zinc-800 rounded-lg p-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode("kanban")}
              className={cn(
                "h-8 px-3",
                viewMode === "kanban" 
                  ? "bg-zinc-700 text-zinc-50" 
                  : "text-zinc-400 hover:text-zinc-50"
              )}
              data-testid="view-kanban-btn"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setViewMode("table")}
              className={cn(
                "h-8 px-3",
                viewMode === "table" 
                  ? "bg-zinc-700 text-zinc-50" 
                  : "text-zinc-400 hover:text-zinc-50"
              )}
              data-testid="view-table-btn"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
          
          <Button
            onClick={() => setAddDialogOpen(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="add-candidate-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Candidate
          </Button>
        </div>
      </div>

      {/* KPI Card for Art Directors */}
      {kpiProgress && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm text-zinc-400 flex items-center gap-2">
              <Target className="h-4 w-4 text-orange-500" />
              My Recruitment Progress - {new Date().toLocaleDateString("en-US", { month: "long", year: "numeric" })}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-6">
              <div>
                <div className="flex justify-between text-xs text-zinc-400 mb-1">
                  <span>Candidates Added</span>
                  <span>{kpiProgress.added.current}/{kpiProgress.added.target}</span>
                </div>
                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <div 
                    className={cn(
                      "h-full rounded-full transition-all",
                      kpiProgress.added.percent >= 100 ? "bg-green-500" :
                      kpiProgress.added.percent >= 70 ? "bg-blue-500" :
                      kpiProgress.added.percent >= 40 ? "bg-yellow-500" :
                      "bg-red-500"
                    )}
                    style={{ width: `${kpiProgress.added.percent}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-xs text-zinc-400 mb-1">
                  <span>Candidates Approved</span>
                  <span>{kpiProgress.approved.current}/{kpiProgress.approved.target}</span>
                </div>
                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                  <div 
                    className={cn(
                      "h-full rounded-full transition-all",
                      kpiProgress.approved.percent >= 100 ? "bg-green-500" :
                      kpiProgress.approved.percent >= 70 ? "bg-blue-500" :
                      kpiProgress.approved.percent >= 40 ? "bg-yellow-500" :
                      "bg-red-500"
                    )}
                    style={{ width: `${kpiProgress.approved.percent}%` }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            placeholder="Search candidates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800 text-zinc-50"
            data-testid="search-input"
          />
        </div>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-800 text-zinc-50">
            <Filter className="h-4 w-4 mr-2 text-zinc-500" />
            <SelectValue placeholder="Source" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            <SelectItem value="all" className="text-zinc-50">All Sources</SelectItem>
            {SOURCES.map(s => (
              <SelectItem key={s.id} value={s.id} className="text-zinc-50">{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={specFilter} onValueChange={setSpecFilter}>
          <SelectTrigger className="w-[180px] bg-zinc-900 border-zinc-800 text-zinc-50">
            <User className="h-4 w-4 mr-2 text-zinc-500" />
            <SelectValue placeholder="Specialization" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            <SelectItem value="all" className="text-zinc-50">All Specializations</SelectItem>
            {specializations.map(s => (
              <SelectItem key={s.id} value={s.id} className="text-zinc-50">{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[160px] bg-zinc-900 border-zinc-800 text-zinc-50">
            <Star className="h-4 w-4 mr-2 text-zinc-500" />
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-800 border-zinc-700">
            <SelectItem value="activity" className="text-zinc-50">Recent Activity</SelectItem>
            <SelectItem value="rating" className="text-zinc-50">Talent Score</SelectItem>
            <SelectItem value="name" className="text-zinc-50">Name A-Z</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-20 text-zinc-500">Loading candidates...</div>
      ) : viewMode === "kanban" ? (
        /* Kanban Board View */
        <div className="space-y-4">
          {/* Main Pipeline */}
          <div className="overflow-x-auto pb-4">
            <div className="flex gap-3 min-w-max">
              {KANBAN_STAGES.map((stage) => (
                <KanbanColumn
                  key={stage.id}
                  stage={stage}
                  candidates={candidatesByStage[stage.id] || []}
                  onDragOver={(e) => handleColumnDragOver(e, stage.id)}
                  onDrop={handleDrop}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onCardClick={(id) => navigate(`/candidates/${id}`)}
                  onQuickEdit={handleQuickEdit}
                  onDelete={setDeleteCandidate}
                  isDragOver={dragOverStage === stage.id}
                  specializations={specializations}
                />
              ))}
            </div>
          </div>
          
          {/* Final Stages (Approved/Rejected) */}
          <div>
            <h3 className="text-sm font-medium text-zinc-400 mb-2">Completed</h3>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {FINAL_STAGES.map((stage) => (
                <KanbanColumn
                  key={stage.id}
                  stage={stage}
                  candidates={candidatesByStage[stage.id] || []}
                  onDragOver={(e) => handleColumnDragOver(e, stage.id)}
                  onDrop={handleDrop}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onCardClick={(id) => navigate(`/candidates/${id}`)}
                  onQuickEdit={handleQuickEdit}
                  onDelete={setDeleteCandidate}
                  isDragOver={dragOverStage === stage.id}
                  specializations={specializations}
                />
              ))}
            </div>
          </div>
        </div>
      ) : (
        /* Table View */
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-zinc-400 text-xs">Candidate</TableHead>
                  <TableHead className="text-zinc-400 text-xs">Specializations</TableHead>
                  <TableHead className="text-zinc-400 text-xs">Stage</TableHead>
                  <TableHead className="text-zinc-400 text-xs text-center">Rating</TableHead>
                  <TableHead className="text-zinc-400 text-xs">Service Rates</TableHead>
                  <TableHead className="text-zinc-400 text-xs">Notes</TableHead>
                  <TableHead className="text-zinc-400 text-xs">Portfolio</TableHead>
                  <TableHead className="text-zinc-400 text-xs">Last Activity</TableHead>
                  <TableHead className="text-zinc-400 text-xs w-[40px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {candidates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-zinc-500">
                      No candidates found
                    </TableCell>
                  </TableRow>
                ) : (
                  candidates.map((candidate) => {
                    const stageConfig = getStageConfig(candidate.candidate_stage);
                    const profile = candidate.candidate_profile || {};
                    const portfolioLinks = profile.portfolio_links || [];
                    const serviceRates = profile.service_rates || [];
                    const formattedRates = serviceRates.slice(0, 3).map(formatServiceRate).filter(Boolean);
                    const allRates = serviceRates.map(formatServiceRate).filter(Boolean);
                    const lastNote = (candidate.evaluation_history || []).slice(-1)[0];
                    const lastActivity = candidate.updated_at || candidate.created_at;
                    
                    return (
                      <TableRow
                        key={candidate.id}
                        className="border-zinc-800 hover:bg-zinc-800/50 cursor-pointer"
                        onClick={() => navigate(`/candidates/${candidate.id}`)}
                        data-testid={`candidate-row-${candidate.id}`}
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {(profile.work_samples_data?.[0]?.thumbnail || profile.work_samples?.[0]) ? (
                              <img 
                                src={profile.work_samples_data?.[0]?.thumbnail || `${profile.work_samples[0]}/thumb`} 
                                alt="" 
                                className="w-9 h-9 rounded-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <div className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center">
                                <User className="h-4 w-4 text-zinc-500" />
                              </div>
                            )}
                            <div>
                              <p className="text-zinc-50 font-medium text-sm">{candidate.name}</p>
                              <p className="text-xs text-zinc-500">{candidate.email || "No email"}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(candidate.specializations || []).slice(0, 3).map((spec, i) => (
                              <Badge key={i} variant="outline" className="text-[10px] bg-zinc-800/50 text-zinc-400 border-zinc-700">
                                {spec}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Select
                            value={candidate.candidate_stage}
                            onValueChange={(v) => handleStageChange(candidate.id, v)}
                          >
                            <SelectTrigger className={cn(
                              "h-7 w-[130px] text-xs border-0",
                              stageConfig.color,
                              "text-zinc-50"
                            )}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-800 border-zinc-700">
                              {ALL_STAGES.map(s => (
                                <SelectItem key={s.id} value={s.id} className="text-zinc-50 text-xs">
                                  {s.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell className="text-center">
                          {profile.skill_rating ? (
                            <div className={cn(
                              "inline-flex items-center gap-1 px-2 py-0.5 rounded",
                              profile.skill_rating >= 8 ? "bg-green-500/20" :
                              profile.skill_rating >= 6 ? "bg-yellow-500/20" :
                              "bg-red-500/20"
                            )}>
                              <Star className={cn(
                                "h-3 w-3 fill-current",
                                profile.skill_rating >= 8 ? "text-green-400" :
                                profile.skill_rating >= 6 ? "text-yellow-400" :
                                "text-red-400"
                              )} />
                              <span className={cn(
                                "text-sm font-bold",
                                profile.skill_rating >= 8 ? "text-green-400" :
                                profile.skill_rating >= 6 ? "text-yellow-400" :
                                "text-red-400"
                              )}>{profile.skill_rating}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-zinc-600">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <TooltipProvider>
                            {formattedRates.length > 0 ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-1 flex-wrap cursor-help max-w-[180px]">
                                    {formattedRates.map((rate, i) => (
                                      <span key={i} className="text-[10px] text-green-400 bg-green-500/10 px-1.5 py-0.5 rounded whitespace-nowrap">
                                        {rate.short}
                                      </span>
                                    ))}
                                    {serviceRates.length > 3 && (
                                      <span className="text-[10px] text-zinc-500">+{serviceRates.length - 3}</span>
                                    )}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="bottom" className="bg-zinc-900 border-zinc-700 p-2">
                                  <div className="space-y-1">
                                    <p className="text-xs font-medium text-zinc-300 mb-1">All Service Rates:</p>
                                    {allRates.map((rate, i) => (
                                      <div key={i} className="text-xs text-zinc-400">
                                        {rate.name}: <span className="text-green-400 font-medium">{rate.price}</span>
                                      </div>
                                    ))}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            ) : (
                              <span className="text-xs text-zinc-600">—</span>
                            )}
                          </TooltipProvider>
                        </TableCell>
                        <TableCell>
                          {(lastNote?.notes || lastNote?.note) ? (
                            <p className="text-xs text-zinc-400 max-w-[150px] truncate" title={lastNote.notes || lastNote.note}>
                              {lastNote.notes || lastNote.note}
                            </p>
                          ) : (
                            <span className="text-xs text-zinc-600">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <TooltipProvider>
                            <div className="flex items-center gap-1.5">
                              {portfolioLinks.length > 0 ? (
                                portfolioLinks.slice(0, 4).map((link, i) => {
                                  const info = getPortfolioIcon(link);
                                  return (
                                    <Tooltip key={i}>
                                      <TooltipTrigger asChild>
                                        <a
                                          href={link}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          className={cn(
                                            "text-base hover:scale-125 transition-transform",
                                            info?.color
                                          )}
                                        >
                                          {info?.icon}
                                        </a>
                                      </TooltipTrigger>
                                      <TooltipContent side="bottom" className="bg-zinc-900 border-zinc-700">
                                        <p className="text-xs">{info?.label}</p>
                                      </TooltipContent>
                                    </Tooltip>
                                  );
                                })
                              ) : (
                                <span className="text-xs text-zinc-600">—</span>
                              )}
                              {portfolioLinks.length > 4 && (
                                <span className="text-[10px] text-zinc-500">+{portfolioLinks.length - 4}</span>
                              )}
                            </div>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-zinc-500">
                            {getRelativeTime(lastActivity) || "—"}
                          </span>
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            {/* Quick Edit Button with Popover */}
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-7 w-7 p-0 text-zinc-400 hover:text-orange-400"
                                >
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent 
                                className="w-72 bg-zinc-900 border-zinc-700 p-3" 
                                side="left"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <TableQuickEdit 
                                  candidate={candidate}
                                  specializations={specializations}
                                  onSave={handleQuickEdit}
                                />
                              </PopoverContent>
                            </Popover>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate(`/candidates/${candidate.id}`)}
                              className="h-7 w-7 p-0 text-zinc-400 hover:text-zinc-50"
                            >
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                            {candidate.candidate_stage !== "approved" && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setDeleteCandidate(candidate)}
                                className="h-7 w-7 p-0 text-zinc-400 hover:text-red-400"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Add Candidate Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Add New Candidate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-zinc-400 text-xs">Name *</Label>
              <Input
                value={newCandidate.name}
                onChange={(e) => setNewCandidate({ ...newCandidate, name: e.target.value })}
                placeholder="Full name"
                className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 text-xs">Email</Label>
                <Input
                  value={newCandidate.email}
                  onChange={(e) => setNewCandidate({ ...newCandidate, email: e.target.value })}
                  placeholder="email@example.com"
                  className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs">Telegram</Label>
                <Input
                  value={newCandidate.telegram}
                  onChange={(e) => setNewCandidate({ ...newCandidate, telegram: e.target.value })}
                  placeholder="@username"
                  className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1"
                />
              </div>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Portfolio Links (one per line)</Label>
              <Textarea
                value={newCandidate.portfolio_links}
                onChange={(e) => setNewCandidate({ ...newCandidate, portfolio_links: e.target.value })}
                placeholder="https://artstation.com/..."
                className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1 min-h-[80px]"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Source</Label>
              <Select
                value={newCandidate.source}
                onValueChange={(v) => setNewCandidate({ ...newCandidate, source: v })}
              >
                <SelectTrigger className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {SOURCES.map(s => (
                    <SelectItem key={s.id} value={s.id} className="text-zinc-50">{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Source Details</Label>
              <Input
                value={newCandidate.source_details}
                onChange={(e) => setNewCandidate({ ...newCandidate, source_details: e.target.value })}
                placeholder="e.g., Found on ArtStation, Referred by John..."
                className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1"
              />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">Internal Notes</Label>
              <Textarea
                value={newCandidate.notes}
                onChange={(e) => setNewCandidate({ ...newCandidate, notes: e.target.value })}
                placeholder="Any initial observations..."
                className="bg-zinc-950 border-zinc-700 text-zinc-50 mt-1"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setAddDialogOpen(false)} className="border-zinc-700">
              Cancel
            </Button>
            <Button onClick={handleAddCandidate} className="bg-orange-500 hover:bg-orange-600 text-white">
              Add Candidate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Candidate Confirmation Dialog */}
      <AlertDialog open={!!deleteCandidate} onOpenChange={(open) => !open && setDeleteCandidate(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-zinc-50">Удалить кандидата?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Вы уверены, что хотите удалить кандидата <span className="text-zinc-50 font-medium">{deleteCandidate?.name}</span>? 
              Это действие нельзя отменить.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 text-zinc-300 hover:text-zinc-50">
              Отмена
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleDeleteCandidate(deleteCandidate?.id)}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CandidatesPage;
